import { z } from 'zod';

const reasonCodes = z.array(z.string().min(1));

export const measurementCampaignProvenanceSchema = z.strictObject({
  parameters_hash: z.string().nullable(),
  tool: z.string().nullable(),
  tool_version: z.string().nullable(),
  measurement_date: z.iso.date().nullable(),
  imported_at: z.iso.datetime(),
  file_checksums: z.record(z.string(), z.string()),
  schema_versions: z.record(z.string(), z.string()),
});

const plotMapping = z.strictObject({
  id: z.number().int().positive(),
  sample_plot: z.number().int().positive(),
  stand_id: z.string().min(1),
  stand_number: z.string().min(1).nullable(),
  overlap_fraction: z.number(),
  mapping_method: z.string().min(1),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const calculation = z.strictObject({
  id: z.number().int().positive(),
  sample_plot: z.number().int().positive(),
  species_code: z.number().int(),
  basal_area_m2_ha: z.number(),
  trees_per_ha: z.number(),
  mean_diameter_cm: z.number().nullable(),
  mean_height_m: z.number().nullable(),
  volume_m3_ha: z.number().nullable(),
  sawlog_volume_m3_ha: z.number().nullable(),
  pulpwood_volume_m3_ha: z.number().nullable(),
  sawlog_percent: z.number().nullable(),
  source: z.string().min(1),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const tree = z.strictObject({
  id: z.number().int().positive(),
  sample_plot: z.number().int().positive(),
  tree_id_external: z.string(),
  species_code: z.number().int(),
  diameter_cm: z.number().positive(),
  height_m: z.number().nullable(),
  height_is_measured: z.boolean(),
  relative_x: z.number().nullable(),
  relative_y: z.number().nullable(),
  relative_z: z.number().nullable(),
  gps_latitude: z.number().nullable(),
  gps_longitude: z.number().nullable(),
  damage_status: z.string(),
  stem_form: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const mapPosition = z.tuple([z.number(), z.number()]);
const mapGeometry = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(mapPosition)),
  }),
  z.strictObject({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(z.array(mapPosition))),
  }),
]);

export const measurementCampaignMapContextSchema = z.strictObject({
  status: z.enum(['available', 'partial', 'unavailable']),
  reason_codes: reasonCodes,
  property_id: z.string().min(1),
  inventory_revision: z.string().regex(/^[a-f0-9]{64}$/),
  stands: z.array(z.strictObject({
    stand_id: z.string().min(1),
    stand_number: z.string().min(1).nullable(),
    dominant_species_code: z.number().int().positive().nullable(),
    geometry: mapGeometry,
  })),
}).superRefine((value, context) => {
  const valid = value.status === 'available'
    ? value.stands.length > 0 && value.reason_codes.length === 0
    : value.status === 'partial'
      ? value.stands.length > 0 && value.reason_codes.length > 0
      : value.stands.length === 0 && value.reason_codes.length > 0;
  if (!valid) {
    context.addIssue({
      code: 'custom',
      message: 'Campaign map context availability payload is contradictory',
    });
  }
});

export const measurementPlotGeometrySchema = z.strictObject({
  center_latitude: z.number().nullable(),
  center_longitude: z.number().nullable(),
  radius_m: z.number().nullable(),
  scan_width_m: z.number().nullable(),
  scan_length_m: z.number().nullable(),
  width_m: z.number().nullable(),
  length_m: z.number().nullable(),
  bearing_deg: z.number().nullable(),
  altitude_m: z.number().nullable(),
  inferred: z.boolean(),
});

export const measurementCampaignPlotSchema = z.strictObject({
  id: z.number().int().positive(),
  sample_id_external: z.string().min(1),
  name: z.string(),
  area_m2: z.number().positive(),
  plot_shape: z.string().min(1),
  measure_method_type: z.string().min(1),
  measurement_method: z.string(),
  geometry: measurementPlotGeometrySchema,
  mapping_status: z.enum(['mapped', 'needs_review', 'unmapped']),
  mapping_reason_codes: reasonCodes,
  primary_mapping: plotMapping.nullable(),
  mappings: z.array(plotMapping),
  calculations: z.array(calculation),
  trees: z.array(tree),
  auxiliary_rows: z.strictObject({
    heights: z.array(z.strictObject({
      sample_id: z.string().min(1),
      height_id: z.string().nullable(),
      tree_id: z.string().nullable(),
      species_code: z.number().int().nullable(),
      height_m: z.number().nullable(),
      marking_x_m: z.number().nullable(),
      marking_y_m: z.number().nullable(),
      marking_z_m: z.number().nullable(),
      growth_cm: z.number().nullable(),
      age_years: z.number().nullable(),
    })),
    stems: z.array(z.strictObject({
      sample_id: z.string().min(1),
      stem_id: z.string().nullable(),
      tree_id: z.string().nullable(),
      diameter_cm: z.number().nullable(),
      x_m: z.number().nullable(),
      y_m: z.number().nullable(),
      z_m: z.number().nullable(),
      quality_code: z.string().nullable(),
    })),
  }),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
